import type { CardDto, ColumnDto } from '@kanban/shared';
import {
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { boardKeyboardCoordinates } from './keyboard-coordinates.js';
import { useCallback, useRef, useState } from 'react';
import {
  TRASH_DROPPABLE_ID,
  dropActionFromOver,
  positionForDrop,
  type DropAction,
  type DropTarget,
} from '../../lib/drag-position.js';

export interface MovePayload {
  cardId: string;
  target: DropTarget;
  position: string;
}

/**
 * Das Verhalten aus SPEC 8.2, gebuendelt und unabhaengig vom Board — damit es
 * sich auf der Testseite `/dnd-labor` isoliert bauen und auf dem Geraet tunen
 * laesst, bevor es ins echte Board wandert (SPEC 16).
 */
export function useBoardDnd({
  columns,
  onMove,
  onTrash,
}: {
  columns: readonly ColumnDto[];
  onMove: (payload: MovePayload) => void;
  /** Ablegen im Papierkorb — archiviert die Karte (SPEC 5.2). */
  onTrash: (cardId: string) => void;
}) {
  const [activeCard, setActiveCard] = useState<CardDto | null>(null);
  const [dropAction, setDropAction] = useState<DropAction | null>(null);
  /**
   * Ob das laufende Ziehen mit Finger oder Maus passiert. Nur dann wird der
   * Papierkorb eingeblendet — per Tastatur ist er nicht erreichbar, und eine
   * sichtbare Zone, die auf keine Taste reagiert, ist schlechter als keine.
   */
  const [pointerDrag, setPointerDrag] = useState(false);
  const cardHeight = useRef(72);
  /** Letzte Zeigerposition waehrend des Ziehens; null beim Tastatur-Drag. */
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const trackPointer = useRef((event: PointerEvent) => {
    pointer.current = { x: event.clientX, y: event.clientY };
  }).current;

  const sensors = useSensors(
    // Long-Press auf Touch: kuerzer kollidiert mit dem Spalten-Wischen
    // (SPEC 8.2.1). Die Toleranz erlaubt das uebliche Wackeln des Fingers.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    // Mit der Maus sofort, sobald es eine echte Zieh-Geste ist.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    // Barrierefreiheit: Leertaste aufnehmen, Pfeiltasten bewegen, Leertaste
    // ablegen (SPEC 8.2). Enter bleibt bewusst unbelegt — damit oeffnet die
    // Karte ihr Detail, sonst gaebe es dafuer keine Tastaturgeste mehr.
    useSensor(KeyboardSensor, {
      coordinateGetter: boardKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  /**
   * `pointerWithin` trifft auf Touch deutlich besser als abstandsbasierte
   * Verfahren, weil der Finger die Karte teilweise verdeckt — es setzt aber
   * einen Zeiger voraus. Beim Ziehen per Tastatur gibt es keinen, dort faende
   * es nie ein Ziel und die Karte liesse sich gar nicht verschieben.
   *
   * Liegt ein Zeiger vor, bleibt es bei `pointerWithin` — auch wenn es leer
   * ausgeht: ausserhalb jeder gueltigen Zone loslassen muss folgenlos bleiben
   * (SPEC 8.2.8).
   */
  const collisionDetection: CollisionDetection = useCallback((args) => {
    if (pointer.current === null) {
      /*
       * Tastatur-Drag: der Papierkorb faellt hier heraus, sonst koennte ihn
       * `closestCorners` als naechstgelegenes Ziel waehlen und eine Karte
       * archivieren, die nur eine Spalte weiter sollte.
       */
      return closestCorners({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => container.id !== TRASH_DROPPABLE_ID,
        ),
      });
    }

    /*
     * Der Papierkorb liegt ueber der Spalte. Welches der beiden Rechtecke
     * `pointerWithin` zurueckgibt, haengt sonst am Abstand zum jeweiligen
     * Mittelpunkt — also an Groessenverhaeltnissen, nicht an der Absicht.
     * Deshalb hier ausdruecklich: Zeiger in der Zone schlaegt alles andere.
     */
    const trash = args.droppableRects.get(TRASH_DROPPABLE_ID);
    const { x, y } = pointer.current;
    if (trash && x >= trash.left && x <= trash.right && y >= trash.top && y <= trash.bottom) {
      const container = args.droppableContainers.find(
        (candidate) => candidate.id === TRASH_DROPPABLE_ID,
      );
      if (container) {
        return [{ id: TRASH_DROPPABLE_ID, data: { droppableContainer: container, value: 0 } }];
      }
    }

    return pointerWithin(args);
  }, []);

  const findCard = useCallback(
    (cardId: string): CardDto | null =>
      columns.flatMap((column) => column.cards).find((card) => card.id === cardId) ?? null,
    [columns],
  );

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const card = findCard(String(event.active.id));
      if (!card) return;

      const rect = event.active.rect.current.initial;
      if (rect?.height) cardHeight.current = rect.height;

      setActiveCard(card);
      pointer.current = null;
      // Das ausloesende Ereignis sagt sofort, womit gezogen wird — auf die
      // erste Zeigerbewegung zu warten hiesse, die Zone erst nach einem
      // Ruck einzublenden.
      setPointerDrag(!(event.activatorEvent instanceof KeyboardEvent));
      window.addEventListener('pointermove', trackPointer);

      // Haptisches Feedback beim Aufnehmen, wo das Geraet es kann.
      navigator.vibrate?.(10);
    },
    [findCard, trackPointer],
  );

  /**
   * Bewusst an `onDragMove` gehaengt, nicht an `onDragOver`: letzteres feuert
   * nur, wenn das Ziel WECHSELT. Innerhalb derselben Karte bliebe die
   * Zeigerposition dann veraltet, und das Einfuegen landete verlaesslich eine
   * Position zu frueh.
   */
  const onDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { active, over } = event;
      if (!over) {
        setDropAction(null);
        return;
      }

      /*
       * Zeigt der Zeiger auf die untere Haelfte der Zielkarte, wird dahinter
       * eingefuegt, sonst davor (SPEC 8.2.4).
       *
       * Gemessen wird die echte Zeigerposition. Die naheliegende Rechnung aus
       * Ausgangsrechteck plus `event.delta` weicht um die Scroll-Korrektur ab,
       * die dnd-kit in `delta` einrechnet — das ergibt beim Ablegen weiter
       * unten in der Spalte verlaesslich eine Position zu frueh.
       *
       * Beim Ziehen per Tastatur gibt es keinen Zeiger; dort liefert dnd-kit
       * das verschobene Rechteck, das dann einspringt.
       */
      const overRect = over.rect;
      const translated = active.rect.current.translated;
      const y =
        pointer.current?.y ?? (translated ? translated.top + translated.height / 2 : null);
      const below = y !== null && overRect ? y > overRect.top + overRect.height / 2 : false;

      setDropAction(dropActionFromOver(columns, String(active.id), String(over.id), below));
    },
    [columns],
  );

  const reset = useCallback(() => {
    window.removeEventListener('pointermove', trackPointer);
    pointer.current = null;
    setActiveCard(null);
    setDropAction(null);
    setPointerDrag(false);
  }, [trackPointer]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const cardId = String(event.active.id);
      const action = dropAction;
      reset();

      // Ausserhalb einer gueltigen Zone losgelassen -> Ruecksprung (SPEC 8.2.8).
      if (!action) return;

      if (action.kind === 'trash') {
        onTrash(cardId);
        return;
      }

      const { target } = action;
      const card = findCard(cardId);
      if (!card) return;

      // Ablage an derselben Stelle nicht an den Server schicken. Der eigene
      // Index in der Liste ohne die Karte selbst ist die Zahl der Karten, die
      // lexikografisch vor ihr liegen.
      if (target.columnId === card.columnId) {
        const others =
          columns.find((column) => column.id === card.columnId)?.cards.filter((c) => c.id !== cardId) ??
          [];
        const ownIndex = others.filter((c) => c.position < card.position).length;
        if (target.index === ownIndex) return;
      }

      onMove({ cardId, target, position: positionForDrop(columns, cardId, target) });
    },
    [columns, dropAction, findCard, onMove, onTrash, reset],
  );

  return {
    sensors,
    activeCard,
    /** Einfuegemarke — beim Papierkorb bewusst keine. */
    dropTarget: dropAction?.kind === 'move' ? dropAction.target : null,
    overTrash: dropAction?.kind === 'trash',
    showTrash: activeCard !== null && pointerDrag,
    cardHeight: cardHeight.current,
    dndContextProps: {
      sensors,
      collisionDetection,
      /*
       * Auto-Scroll nur vertikal, innerhalb einer Spalte (SPEC 8.2.5).
       *
       * Horizontal uebernimmt das Blaettern nach Verweildauer (SPEC 8.2.6).
       * Beides zugleich waere ein Kampf gegen das Scroll-Snap der Spalten:
       * dnd-kit beschleunigt bis ans Ende des Boards, waehrend die Spalten
       * einrasten wollen — treffen kann man dabei nichts mehr.
       */
      autoScroll: {
        canScroll: (element: Element) =>
          !(element instanceof HTMLElement && element.classList.contains('board-scroller')),
      },
      // Fortlaufend messen statt nur beim Aufnehmen: der eingeschobene
      // Platzhalter verschiebt die Karten waehrend des Ziehens. Mit den
      // Startmassen zielt die Kollisionserkennung daneben, und zwar umso mehr,
      // je weiter unten in der Spalte man ablegt.
      measuring: { droppable: { strategy: MeasuringStrategy.Always } },
      onDragStart,
      onDragMove,
      onDragOver: onDragMove,
      onDragEnd,
      onDragCancel: reset,
    },
  };
}
